import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { useObjectUrl } from "@/hooks/useObjectUrl"
import { apiClientV2 } from "@/services/api/apiClient"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

// Settings > Declaration templates (phase-2 design,
// docs/superpowers/specs/2026-09-22-conformity-template-editor-phase2-design.md).
// One template per org this phase, addressed by key "default". The tree is
// Tiptap/ProseMirror JSON; the API client's snake<->camel pass converts the
// backend's `text_align` / `font_size` / `accent_color` into the camelCase
// attribute names Tiptap's own extensions use, so no mapping layer here.
const BASE = "/material-compliance/declaration-templates"

export type TemplateLang = "de" | "en"
export const TEMPLATE_LANGS: TemplateLang[] = ["de", "en"]
/** The template's other language — a restore stamps both (design §6). */
export const otherLang = (lang: TemplateLang): TemplateLang =>
  lang === "de" ? "en" : "de"
export const DEFAULT_TEMPLATE_KEY = "default"

export const declarationTemplateKeys = {
  all: ["mc", "declaration-templates"] as const,
  cards: ["mc", "declaration-templates", "cards"] as const,
  draft: (key: string, lang: TemplateLang) =>
    ["mc", "declaration-templates", key, "draft", lang] as const,
  versions: (key: string) =>
    ["mc", "declaration-templates", key, "versions"] as const,
  versionDraft: (key: string, versionId: string | null, lang: TemplateLang) =>
    [
      "mc",
      "declaration-templates",
      key,
      "versions",
      versionId,
      "draft",
      lang,
    ] as const,
  variables: (lang: TemplateLang) =>
    ["mc", "declaration-templates", "variables", lang] as const,
  asset: (id: string) => ["mc", "declaration-templates", "asset", id] as const,
  variantPreview: (
    key: string,
    lang: TemplateLang,
    variant: TemplateWordingRef | null,
  ) =>
    [
      "mc",
      "declaration-templates",
      key,
      "variant-preview",
      lang,
      variant?.atom ?? null,
      variant?.key ?? null,
    ] as const,
  /** Mutation key of every draft save (autosave, theme, header & footer):
   *  `useIsMutating` on it tells whether one is still in flight. */
  saveDraft: ["mc", "declaration-templates", "save-draft"] as const,
}

export interface TemplateVersion {
  id: string
  version: number
  publishedAt: string
  publishedBy: string | null
  publishedByName: string | null
  note: string | null
}

export interface TemplateCard {
  key: string
  name: string
  /** The template generate pins when none is chosen. */
  isDefault: boolean
  currentVersion: TemplateVersion | null
  unpublishedChanges: Record<TemplateLang, boolean>
  /** Template-level setting, not part of a published version (design §5) —
   *  `generate_v2` pins this signature when a declaration's draft has none.
   *  Optional (not just nullable) so fixtures built before this field
   *  existed keep type-checking; the backend always sends it. */
  defaultSignatureId?: string | null
}

/** The layouts a declaration can be pinned to: every published template,
 *  plus the built-in layout (`default`), which prints until it publishes.
 *  The conformity switchers show once there are two to choose from. */
export function pickableTemplates(cards: TemplateCard[]): TemplateCard[] {
  return cards.filter(
    (t) => t.currentVersion !== null || t.key === DEFAULT_TEMPLATE_KEY,
  )
}

/** A column a regulation's detail table can print: its row field and its
 *  default header in the draft's language (feedback design §M4). */
export interface TemplateTableField {
  field: string
  label: string
}

export interface TemplateFramework {
  key: string
  title: string
  /** Optional (not just empty) so fixtures built before this field existed
   *  keep type-checking; the backend always sends it. */
  tableFields?: TemplateTableField[]
}

/** One printed column of a regulation's table: the field, and the org's own
 *  header (`null` = the default one). */
export interface TemplateTableColumn {
  field: string
  label: string | null
}

/** A regulation's table layout on the frameworkBlocks atom (`attrs.tables`),
 *  keyed by framework in a list entry (the casing interceptor would mangle
 *  `REACH_SVHC` as an object key). */
export interface TemplateTableConfig {
  key: string
  columns: TemplateTableColumn[]
}

/** One edited variant of an atom's turnus copy (`attrs.wording` entries and
 *  the draft's `wordingDefaults`). A list, not a map: the API client
 *  camelCases object KEYS, and variant keys like `customer_by_reference`
 *  must survive the wire as-is. */
/** One wording variant of a data atom: `scopeStatement` + `products_many`,
 *  `documentHead` + `subtitle:customer`, `frameworkBlocks` +
 *  `statement:ROHS:scoped`. */
export interface TemplateWordingRef {
  atom: string
  key: string
}

export interface TemplateWordingEntry {
  key: string
  nodes: TemplateNode[]
}

export interface TemplateAsset {
  id: string
  contentType: string
  width: number
  height: number
}

/** The look of every table of the declaration (feedback design §M4); the
 *  defaults are today's look. */
export interface TemplateTableStyle {
  header: "plain" | "accent" | "grey"
  borders: "rows" | "grid" | "none"
  zebra: boolean
  size: "normal" | "small"
}

export interface TemplateTheme {
  accentColor: string
  borderRadius: number
  /** `null`/absent: the default table look. */
  table?: TemplateTableStyle | null
}

/** Per-render page margins (design §4): grows with the header/footer strip
 *  content, min 12mm top / 20mm bottom with none, capped at 40mm; left/right
 *  fixed at 15mm. The ONE shape both the draft and the conformity
 *  block-editor response carry (`ConformityBlockDocument.pageGeometry` in
 *  conformityApi.ts reuses this type) — computed server-side by
 *  `page_margins_mm`, never by the client. */
export interface PageGeometry {
  marginsMm: { top: number; right: number; bottom: number; left: number }
}

/** ProseMirror JSON as the backend validates it (mc_schemas.TemplateDocIn),
 *  camelCased on the wire. Loosely typed on purpose: Tiptap owns the shape. */
export interface TemplateNode {
  type: string
  attrs?: Record<string, unknown>
  content?: TemplateNode[]
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>
  text?: string
}

export interface TemplateDoc {
  type: "doc"
  content: TemplateNode[]
}

export interface TemplateDraft {
  key: string
  name: string
  lang: TemplateLang
  blocks: TemplateDoc
  /** Every data atom's sample HTML, in the tree or not — what the `/` menu
   *  inserts for a re-added block so it renders without a round trip. */
  atoms: Record<string, string>
  /** Turnus copy per wording variant key (what an unedited variant prints)
   *  and the frameworks with copy in this language — the wording panel. */
  wordingDefaults: TemplateWordingEntry[]
  frameworks: TemplateFramework[]
  /** The wording variants this document actually prints: the sample shows
   *  one scope, one subtitle and one statement per framework; the panel
   *  offers a preview for every other one. Optional for older fixtures. */
  shownWording?: TemplateWordingRef[]
  theme: TemplateTheme
  pageGeometry: PageGeometry
  /** Design §4b: the `pageFooter` node that prints when the tree has none
   *  (today's footer, in this language) — what the header & footer editor
   *  starts from. Optional so fixtures (and a backend without it yet) keep
   *  working; the editor then falls back to an empty footer. */
  defaultPageFooter?: TemplateNode
  unpublishedChanges: Record<TemplateLang, boolean>
  currentVersion: TemplateVersion | null
  /** Template-level setting, not part of a published version (design §5) —
   *  `null` when the org hasn't set one. */
  defaultSignatureId: string | null
  /** Only meaningful on a `copy-layout` response (design §7): whether the
   *  requested auto-translate actually ran and succeeded. `null` when
   *  translation wasn't requested (every other draft response) — optional so
   *  fixtures built before this field existed keep type-checking. */
  translated?: boolean | null
}

export interface TemplateVariable {
  path: string
  type: "text" | "date" | "number" | "email"
  group: string
  /** What the variable prints in the sample declaration (per language). */
  example: string
}

export function useDeclarationTemplates(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: declarationTemplateKeys.cards,
    queryFn: () => apiClientV2.get<TemplateCard[]>(BASE).then((r) => r.data),
    enabled: options.enabled ?? true,
  })
}

export function fetchDeclarationTemplateDraft(
  key: string,
  lang: TemplateLang,
): Promise<TemplateDraft> {
  return apiClientV2
    .get<TemplateDraft>(`${BASE}/${key}/draft`, { params: { lang } })
    .then((r) => r.data)
}

export function useDeclarationTemplateDraft(key: string, lang: TemplateLang) {
  return useQuery({
    queryKey: declarationTemplateKeys.draft(key, lang),
    queryFn: () => fetchDeclarationTemplateDraft(key, lang),
  })
}

export function useDeclarationTemplateVersions(key: string) {
  return useQuery({
    queryKey: declarationTemplateKeys.versions(key),
    queryFn: () =>
      apiClientV2
        .get<TemplateVersion[]>(`${BASE}/${key}/versions`)
        .then((r) => r.data),
  })
}

/** The draft hydrated against a sample declaration switched to `variant`
 *  (a scope, subtitle or framework statement the default sample does not
 *  print). `null` disables the query. Read-only; never cached past the
 *  modal, since the draft it renders keeps changing. */
export function useDeclarationTemplateVariantPreview(
  key: string,
  lang: TemplateLang,
  variant: TemplateWordingRef | null,
) {
  return useQuery({
    queryKey: declarationTemplateKeys.variantPreview(key, lang, variant),
    queryFn: () =>
      apiClientV2
        .get<TemplateDraft>(`${BASE}/${key}/draft`, {
          // Query params skip the case interceptor: snake_case here.
          params: {
            lang,
            preview_atom: variant?.atom,
            preview_key: variant?.key,
          },
        })
        .then((r) => r.data),
    enabled: variant !== null,
    gcTime: 0,
    staleTime: 0,
  })
}

/** A published version hydrated exactly like a draft (design §6, task E1) —
 *  read-only preview, never the live template's `unpublished_changes` (those
 *  keep describing the live draft, not the version being previewed).
 *  `versionId: null` (nothing being previewed) disables the query; the
 *  query key still needs a stable shape, so `null` is part of it rather than
 *  swapped for a placeholder string. */
export function useDeclarationTemplateVersionDraft(
  key: string,
  versionId: string | null,
  lang: TemplateLang,
) {
  return useQuery({
    queryKey: declarationTemplateKeys.versionDraft(key, versionId, lang),
    queryFn: () =>
      apiClientV2
        .get<TemplateDraft>(`${BASE}/${key}/versions/${versionId}/draft`, {
          params: { lang },
        })
        .then((r) => r.data),
    enabled: versionId !== null,
  })
}

export function useDeclarationTemplateVariables(lang: TemplateLang) {
  return useQuery({
    queryKey: declarationTemplateKeys.variables(lang),
    queryFn: () =>
      apiClientV2
        .get<TemplateVariable[]>(`${BASE}/variables`, { params: { lang } })
        .then((r) => r.data),
    staleTime: Infinity,
  })
}

export type SaveDraftInput =
  | { key: string; lang: TemplateLang; blocks: TemplateDoc }
  | { key: string; lang: TemplateLang; theme: TemplateTheme }

/** Autosave — whole tree for one language, or the theme. Guarded by RELEASE:
 *  only who may issue a declaration may change what issued ones look like. */
export function useSaveDeclarationTemplateDraft() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationKey: declarationTemplateKeys.saveDraft,
    mutationFn: ({ key, lang, ...body }: SaveDraftInput) =>
      apiClientV2
        .patch<TemplateDraft>(`${BASE}/${key}/draft`, body, {
          params: { lang },
        })
        .then((r) => r.data),
    onSuccess: () => {
      // The card's "unpublished changes" badge derives from what was just
      // saved; the draft query itself is NOT touched — the editor holds the
      // live tree and remounts on language / sample changes.
      void qc.invalidateQueries({ queryKey: declarationTemplateKeys.cards })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

export function usePublishDeclarationTemplate() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ key, note }: { key: string; note: string | null }) =>
      apiClientV2
        .post<TemplateVersion>(`${BASE}/${key}/publish`, { note })
        .then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: declarationTemplateKeys.all })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

/** Overwrite the draft (both languages + theme) with a published version's
 *  snapshot (design §6, task E1) — guarded like the other RELEASE mutations.
 *  The response is `lang`'s fresh draft: store it so the editor's remount
 *  reads it at once, same reasoning as `useCopyTemplateLayout` below. */
export function useRestoreDeclarationTemplateVersion() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      key,
      versionId,
      lang,
    }: {
      key: string
      versionId: string
      lang: TemplateLang
    }) =>
      apiClientV2
        .post<TemplateDraft>(
          `${BASE}/${key}/versions/${versionId}/restore`,
          null,
          { params: { lang } },
        )
        .then((r) => r.data),
    onSuccess: (data, { key, lang }) => {
      qc.setQueryData(declarationTemplateKeys.draft(key, lang), data)
      // `restore_version` overwrites BOTH languages' drafts, so the other
      // language's cache is now wrong. Removed, not just invalidated (review
      // I2): `TemplateDocument` passes `draft.blocks` as `useEditor`'s
      // `content`, which Tiptap applies ONCE at mount and never re-reads — an
      // invalidated-but-still-cached tree would mount, the next keystroke
      // would autosave it, and the restore would be silently undone for that
      // language. Dropping the entry makes the next mount wait for the server.
      qc.removeQueries({
        queryKey: declarationTemplateKeys.draft(key, otherLang(lang)),
      })
      void qc.invalidateQueries({ queryKey: declarationTemplateKeys.cards })
      void qc.invalidateQueries({
        queryKey: declarationTemplateKeys.versions(key),
      })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

/** The draft rendered with sample data through Gotenberg, as an object URL
 *  the caller owns (revoke it when done). POST: real work, never cached. */
export async function fetchTemplatePreviewPdfUrl(
  key: string,
  lang: TemplateLang,
): Promise<string> {
  const r = await apiClientV2.post(`${BASE}/${key}/preview.pdf`, null, {
    params: { lang },
    responseType: "blob",
  })
  return URL.createObjectURL(r.data as Blob)
}

// ── phase 3: several templates, layout copy, image assets ────────────────────

/** New template = a copy of `sourceKey` (both languages + theme). */
export function useCreateDeclarationTemplate() {
  const qc = useQueryClient()
  const mutation = useMutation({
    /** `sourceKey: null` = a blank page (feedback design §2.2). */
    mutationFn: (body: { name: string; sourceKey: string | null }) =>
      apiClientV2.post<TemplateCard>(BASE, body).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: declarationTemplateKeys.cards })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

/** Rename, make the org default (`isDefault: true`), and/or set or clear the
 *  default signature (design §5). `defaultSignatureId` and
 *  `clearDefaultSignature` are mutually exclusive — clearing is its own flag
 *  rather than `defaultSignatureId: null` so "unset" and "explicitly none"
 *  aren't ambiguous on the wire. */
export function useUpdateDeclarationTemplate() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      key,
      ...body
    }: {
      key: string
      name?: string
      isDefault?: true
      defaultSignatureId?: string
      clearDefaultSignature?: true
    }) =>
      apiClientV2
        .patch<TemplateCard>(`${BASE}/${key}`, body)
        .then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: declarationTemplateKeys.cards })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

/** Soft delete; 409 IS_DEFAULT when it is the org default. */
export function useArchiveDeclarationTemplate() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (key: string) =>
      apiClientV2.delete(`${BASE}/${key}`).then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: declarationTemplateKeys.cards })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

/** Overwrite `toLang`'s draft with `fromLang`'s tree; `translate` (design §7)
 *  asks the backend to auto-translate the org-authored text runs it copies —
 *  the response's `translated` reports whether that actually happened
 *  (`false` on a degrade, `null` when `translate` was false). */
export function useCopyTemplateLayout() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      key,
      ...body
    }: {
      key: string
      fromLang: TemplateLang
      toLang: TemplateLang
      translate: boolean
    }) =>
      apiClientV2
        .post<TemplateDraft>(`${BASE}/${key}/copy-layout`, body)
        .then((r) => r.data),
    onSuccess: (draft, { key, toLang }) => {
      // The response is `toLang`'s fresh draft: store it so the editor's
      // remount reads it at once (a refetch would land after the remount).
      qc.setQueryData(declarationTemplateKeys.draft(key, toLang), draft)
      void qc.invalidateQueries({ queryKey: declarationTemplateKeys.cards })
    },
  })
  return useGuardedMutation(
    mutation,
    PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  )
}

/** PNG/JPEG ≤ 2 MB; the backend reads the real type and dimensions. */
export async function uploadTemplateAsset(file: File): Promise<TemplateAsset> {
  const form = new FormData()
  form.append("file", file)
  const r = await apiClientV2.post<TemplateAsset>(`${BASE}/assets`, form)
  return r.data
}

/** The image bytes through the authenticated client, as an object URL for
 *  `<img src>` (assets are immutable, so cache forever within the session). */
export function useTemplateAssetUrl(assetId: string): string | null {
  const query = useQuery({
    queryKey: declarationTemplateKeys.asset(assetId),
    queryFn: () =>
      apiClientV2
        .get(`${BASE}/assets/${assetId}`, { responseType: "blob" })
        .then((r) => r.data as Blob),
    enabled: !!assetId,
    staleTime: Infinity,
  })
  return useObjectUrl(query.data)
}
